import {
  useState,
  useCallback
} from 'react';

import {
  useMutation,
  useQueryClient
} from '@ubisend/pulse-hooks';
import { useNotification } from '@ubisend/pulse-components';

import {
  deleteResource,
  recrawlResource,
  saveSharePointTags,
  createSharePointTag,
  deleteSharePointSite,
  resyncSharePointSite,
  disconnectSharePoint,
  deleteSharePointFolder,
  resyncSharePointFolder
} from '../../api';

const useSharePointState = () => {
  const { showSuccess, showError } = useNotification();
  const queryClient = useQueryClient();

  // Core state
  const [tokenInvalid, setTokenInvalid] = useState(false);
  const [activeModal, setActiveModal] = useState(null);
  const [selectedSite, setSelectedSite] = useState(null);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [selectedResource, setSelectedResource] = useState(null);
  const [globalLoading, setGlobalLoading] = useState(false);

  // Tags
  const [tags, setTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);

  // Crawl flags (used for polling only)
  const [crawlSites, setCrawlSites] = useState(true);
  const [crawlFolders, setCrawlFolders] = useState(false);
  const [crawlResources, setCrawlResources] = useState(false);

  // Explicit panel visibility
  const [isPanelOpen, setIsPanelOpen] = useState(false);

  // Action-only site (for delete/resync modals)
  const [selectedActionSite, setSelectedActionSite] = useState(null);

  // Helpers
  const refetchStatus = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['generative-ai/sharepoint/status'] });
  }, [queryClient]);

  const refetchSite = useCallback(() => {
    if (selectedSite) {
      queryClient.invalidateQueries({ queryKey: [`generative-ai/sharepoint/site/${selectedSite.id}`] });
    }
  }, [queryClient, selectedSite]);

  const refetchFolder = useCallback(() => {
    if (selectedSite && selectedFolder) {
      queryClient.invalidateQueries({ queryKey: [`generative-ai/sharepoint/site/${selectedSite.id}/folder/${selectedFolder.id}/resource`] });
    }
  }, [queryClient, selectedSite, selectedFolder]);

  const refetchFolderList = useCallback(() => {
    if (selectedSite) {
      queryClient.invalidateQueries({ queryKey: [`generative-ai/sharepoint/site/${selectedSite.id}/folder/list`] });
    }
  }, [queryClient, selectedSite]);

  const refetchResourceTag = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['generative-ai/sharepoint/groups/available'] });
  }, [queryClient]);

  // Mutations
  const disconnectMutation = useMutation({
    mutationFn: disconnectSharePoint,
    onMutate: () => setGlobalLoading(true),
    onSettled: () => setGlobalLoading(false),
    onSuccess: () => {
      setActiveModal(null);
      refetchStatus();
    },
    onError: () => setActiveModal(null)
  });

  const resyncSiteMutation = useMutation({
    mutationFn: () => resyncSharePointSite(selectedActionSite?.id ?? selectedSite?.id),
    onMutate: () => setGlobalLoading(true),
    onSettled: () => setGlobalLoading(false),
    onSuccess: () => {
      setActiveModal(null);
      refetchStatus();
      const id = selectedActionSite?.id ?? selectedSite?.id;
      if (id) {
        queryClient.invalidateQueries({ queryKey: [`generative-ai/sharepoint/site/${id}`] });
      }
      showSuccess('SharePoint folders resync requested.');
    },
    onError: (error) => {
      const message = error?.response?.data?.message;
      if (message === 'Token is invalid, please reconnect.') {
        setActiveModal(null);
        setTokenInvalid(true);
      } else {
        showError('Failed to resync all folders.');
      }
    }
  });

  const resyncFolderMutation = useMutation({
    mutationFn: () => resyncSharePointFolder(selectedSite?.id, selectedFolder?.id),
    onMutate: () => setGlobalLoading(true),
    onSettled: () => setGlobalLoading(false),
    onSuccess: () => {
      setActiveModal(null);
      refetchSite();
      showSuccess(`Folder ${selectedFolder?.name} resync requested.`);
    },
    onError: (error) => {
      const message = error?.response?.data?.message;
      if (message === 'Token is invalid, please reconnect.') {
        setActiveModal(null);
        setTokenInvalid(true);
      } else {
        showError(`Failed to resync folder ${selectedFolder?.name}.`);
      }
    }
  });

  const deleteSiteMutation = useMutation({
    mutationFn: () => deleteSharePointSite(selectedActionSite?.id),
    onMutate: () => setGlobalLoading(true),
    onSettled: () => setGlobalLoading(false),
    onSuccess: () => {
      setActiveModal(null);
      refetchStatus();
      showSuccess(`Site ${selectedActionSite?.site_name} delete requested.`);
      setSelectedActionSite(null);
    },
    onError: () => showError(`Failed to delete site ${selectedActionSite?.site_name}.`)
  });

  const deleteFolderMutation = useMutation({
    mutationFn: () => deleteSharePointFolder(selectedSite?.id, selectedFolder?.id),
    onMutate: () => setGlobalLoading(true),
    onSettled: () => setGlobalLoading(false),
    onSuccess: () => {
      setActiveModal(null);
      refetchSite();
      showSuccess(`Folder ${selectedFolder?.name} delete requested.`);
    },
    onError: () => showError(`Failed to delete folder ${selectedFolder?.name}.`)
  });

  // Resource actions
  const deleteResourceAction = useCallback(async (resourceId) => {
    try {
      return await deleteResource(resourceId);
    } catch (err) {
      showError(`Failed to delete resource: ${err.message}`);
      throw err;
    }
  }, [showError]);

  const recrawlResourceAction = useCallback(async (resourceId) => {
    try {
      return await recrawlResource(resourceId);
    } catch (err) {
      showError(`Failed to recrawl resource: ${err.message}`);
      throw err;
    }
  }, [showError]);

  const saveTagsMutation = useMutation({
    mutationFn: ({ resourceId, groupIds }) => saveSharePointTags({ resourceId, groupIds }),
    onSuccess: () => {
      refetchFolder();
      setActiveModal(null);
    }
  });

  const createTagMutation = useMutation({
    mutationFn: createSharePointTag,
    onSuccess: () => {
      refetchResourceTag();
    }
  });

  return {
    // State
    activeModal,
    queryClient,
    selectedSite,
    selectedFolder,
    selectedResource,
    tokenInvalid,
    globalLoading,
    tags,
    selectedTags,

    // Crawl flags
    crawlSites,
    crawlFolders,
    crawlResources,

    // Panel visibility & action site
    isPanelOpen,
    selectedActionSite,

    // Setters
    setActiveModal,
    setSelectedSite,
    setSelectedFolder,
    setSelectedResource,
    setTokenInvalid,
    setTags,
    setSelectedTags,
    setIsPanelOpen,
    setSelectedActionSite,

    // Crawl setters
    setCrawlSites,
    setCrawlFolders,
    setCrawlResources,

    // Mutations
    deleteSiteMutation,
    resyncSiteMutation,
    deleteFolderMutation,
    resyncFolderMutation,
    disconnectMutation,
    saveTagsMutation,
    createTagMutation,

    // Resource actions
    deleteResource: deleteResourceAction,
    recrawlResource: recrawlResourceAction,

    // Helpers
    refetchSite,
    refetchStatus,
    refetchFolder,
    refetchFolderList,
    refetchResourceTag,

    // Notifications
    showSuccess,
    showError
  };
};

export default useSharePointState;